/* ═══════════════════════════════════════════════════════════════════════════
 * UNE DATE SANS HEURE SE DECOUPE — ELLE NE SE REINTERPRETE PAS — 16/10/2026
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ LA REGLE, POSEE PAR DOM : « une date sans heure se formate en decoupant
 *    AAAA-MM-JJ, jamais via `new Date(...)` / `toISOString()` ».
 *
 * 🔴 POURQUOI, ET CE QUI A ETE MESURE. Deux pieges INDEPENDANTS, c'est ce qui
 *    rend le sujet trompeur :
 *
 *    ① `new Date('2019-04-02')` est interprete en **UTC** par la norme. Affiche
 *       avec `toLocaleDateString()` dans un fuseau NEGATIF, cela rend le
 *       **1er avril**. Mesure du 16/10 : 29 ecrans concernes, 51 appels.
 *    ② `new Date().toISOString().slice(0, 10)` ne rend pas « aujourd'hui » : il
 *       rend « le jour qu'il est a Greenwich ». A Cotonou (UTC+1), entre 00h00
 *       et 01h00, il rend **la veille** — et un formulaire la proposait comme
 *       valeur par defaut. Le creneau fautif dure exactement le decalage du
 *       fuseau : une heure par jour au Benin.
 *
 * ➡️ ICI, AUCUNE FONCTION NE CONSTRUIT DE `Date` A PARTIR D'UNE CHAINE, et
 *    aucune n'appelle `toISOString()`. On decoupe, ou on lit des composantes
 *    locales. *Le resultat ne depend donc d'aucun fuseau — ni celui du serveur,
 *    ni celui du poste.*
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Le jour d'une valeur recue de l'API, en 'AAAA-MM-JJ'.
 *
 * ⓘ Depuis `pg.types.setTypeParser(1082)` (16/10/2026), l'API renvoie deja
 *   'AAAA-MM-JJ'. Cette fonction accepte AUSSI l'ancienne forme
 *   'AAAA-MM-JJTHH:MM:SS.sssZ' : *un ecran ouvert pendant un deploiement peut
 *   recevoir les deux, et il ne doit pas choisir ce jour-la entre juste et
 *   faux.*
 */
export function jourISO(v) {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return '';
    const a = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const j = String(v.getDate()).padStart(2, '0');
    return `${a}-${m}-${j}`;
  }
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

/**
 * Le jour qu'il est sur CE poste, en 'AAAA-MM-JJ'.
 * ⚠️ Remplace `new Date().toISOString().slice(0, 10)` : on lit les composantes
 *    LOCALES, donc le jour que l'utilisateur a sous les yeux.
 */
export function aujourdHui() {
  return jourISO(new Date());
}

/**
 * Une date-jour affichee a la francaise : 'JJ/MM/AAAA'.
 *
 * ⚠️ ELLE REMPLACE LES SIX `formatDate` LOCAUX, qui faisaient tous
 *    `new Date(d).toLocaleDateString('fr-FR')`. Ici, on DECOUPE puis on
 *    REASSEMBLE : aucun instant n'est construit, donc aucun fuseau n'intervient.
 * ⓘ `vide` est ce qu'on affiche quand il n'y a pas de date — '-' par habitude
 *   dans les tableaux.
 */
export function formatDate(v, vide = '-') {
  const j = jourISO(v);
  if (!j) return vide;
  const [annee, mois, jour] = j.split('-');
  return `${jour}/${mois}/${annee}`;
}

/**
 * Une date-jour avec son mois en lettres : '2 avril 2019'.
 * ⓘ Pour les textes et les pieces, la ou 'JJ/MM/AAAA' se lit mal.
 * ⚠️ On n'appelle PAS `toLocaleDateString` : on indexe une table de mois. Un
 *    nom de mois n'a pas besoin d'un instant pour etre choisi.
 */
const MOIS = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
export function formatDateLongue(v, vide = '-') {
  const j = jourISO(v);
  if (!j) return vide;
  const [annee, mois, jour] = j.split('-');
  return `${Number(jour)} ${MOIS[Number(mois) - 1]} ${annee}`;
}

/** Compare deux dates-jours. Rend < 0, 0 ou > 0. */
export function comparerJours(a, b) {
  const x = jourISO(a);
  const y = jourISO(b);
  if (x === y) return 0;
  if (!x) return -1;
  if (!y) return 1;
  /* ⓘ 'AAAA-MM-JJ' se compare lexicographiquement comme chronologiquement —
   *   c'est la raison d'etre de ce format, et c'est pourquoi on n'a besoin
   *   d'aucun objet `Date` pour trancher. */
  return x < y ? -1 : 1;
}
